"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as signalR from "@microsoft/signalr";
import { toast } from "sonner";

import {
  arenaApi,
  type ArenaGame,
  type ArenaPlacementInput,
  type ArenaSeries,
} from "@/shared/api/arena";
import { getHubBase } from "@/shared/lib/signalr-helpers";
import { Button } from "@/shared/ui/buttons/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/inputs/select";
import { Badge } from "@/shared/ui/data-display/badge";

import { ArenaStandingsTable } from "./arena-standings-table";
import { ArenaGameScoreboard } from "./arena-game-scoreboard";
import { extractApiErrorMessage } from "@/shared/api/error-handler";

interface ArenaPanelProps {
  tournamentId: string;
  isManager: boolean;
}

const STATUS_LABEL_KEY: Record<ArenaGame["status"], string> = {
  Pending: "litArenaPanel.ozhidaet",
  Live: "litArenaPanel.idet",
  Completed: "litArenaPanel.zavershena",
  Cancelled: "litArenaPanel.otmenena",
};

const STATUS_VARIANT: Record<ArenaGame["status"], "secondary" | "success" | "info" | "destructive"> = {
  Pending: "secondary",
  Live: "info",
  Completed: "success",
  Cancelled: "destructive",
};

export function ArenaPanel({ tournamentId, isManager }: ArenaPanelProps) {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const qc = useQueryClient();
  const queryKey = ["arena-series", tournamentId];

  const { data: series, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => arenaApi.getSeries(tournamentId),
  });

  // Real-time: подписка на ArenaHub → обновляем кэш без F5.
  useEffect(() => {
    if (!tournamentId) return;
    let mounted = true;
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${getHubBase()}/arena`, { withCredentials: true })
      .configureLogging(signalR.LogLevel.None)
      .withAutomaticReconnect()
      .build();

    conn.on("ArenaSeriesUpdated", (dto: ArenaSeries) => {
      if (mounted && dto?.tournamentId === tournamentId) qc.setQueryData(queryKey, dto);
    });

    // KAN-985: после обрыва — снова в группу турнира (новый ConnectionId, KAN-824)
    // и дозапрос серии: обновления, пришедшие во время обрыва, потеряны, а опроса
    // здесь нет — без этого серия застывала до перезагрузки.
    conn.onreconnected(() => {
      if (!mounted) return;
      conn.invoke("JoinTournament", tournamentId).catch(() => {});
      void qc.invalidateQueries({ queryKey });
    });

    conn
      .start()
      .then(() => mounted && conn.invoke("JoinTournament", tournamentId))
      .catch(() => {});

    return () => {
      mounted = false;
      conn.off("ArenaSeriesUpdated");
      conn.stop().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  const startMutation = useMutation({
    mutationFn: () => arenaApi.startSeries(tournamentId),
    onSuccess: (data) => {
      qc.setQueryData(queryKey, data);
      toast.success(tToast("arenaPanel.seriyaZapuschena"));
    },
    onError: (e: unknown) =>
      toast.error(extractApiErrorMessage(e, tUi("litArenaPanel.neUdalosZapustitSeriyu"))),
  });

  if (isLoading) {
    return <div className="rounded-2xl border bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">{tUi("arenaPanel.zagruzka")}</div>;
  }

  if (error || !series) {
    return (
      <div className="rounded-2xl border bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
        {tUi("arenaPanel.neUdalosZagruzitSeriyu")}
      </div>
    );
  }

  const noSeries = series.games.length === 0;

  return (
    <div className="space-y-6">
      {/* Лидерборд */}
      <Card className="border-0 shadow-none rounded-2xl bg-black/6 dark:bg-white/4">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{tUi("arenaPanel.tablitsa")}</span>
            <span className="text-sm font-normal text-muted-foreground">
              {series.teamCount} команд · {series.gamesCount} игр
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ArenaStandingsTable standings={series.standings} games={series.games} />
        </CardContent>
      </Card>

      {/* Запуск серии */}
      {noSeries && (
        <Card className="border-0 shadow-none rounded-2xl bg-black/6 dark:bg-white/4">
          <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
            <p className="text-sm text-muted-foreground">
              Серия ещё не запущена. После закрытия регистрации запустите {series.gamesCount} игр.
            </p>
            {isManager ? (
              <Button onClick={() => startMutation.mutate()} disabled={startMutation.isPending}>
                {startMutation.isPending ? tUi("litArenaPanel.zapusk") : tUi("litArenaPanel.zapustitSeriyu")}
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">{tUi("arenaPanel.ozhidayteStartaOtOrganizatora")}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Игры */}
      {!noSeries && (
        <div className="space-y-3">
          {series.games.map((game) => (
            <GameCard
              key={game.id}
              game={game}
              series={series}
              isManager={isManager}
              onSubmitted={(data) => qc.setQueryData(queryKey, data)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Карточка одной игры ────────────────────────────────────────────────────────

function GameCard({
  game,
  series,
  isManager,
  onSubmitted,
}: {
  game: ArenaGame;
  series: ArenaSeries;
  isManager: boolean;
  onSubmitted: (data: ArenaSeries) => void;
}) {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const [editing, setEditing] = useState(false);
  const canEdit = isManager && (game.status === "Live" || game.status === "Completed");

  // «Закончили игру N» — серверная подтяжка результата из истории Riot (без приложения).
  const fetchMutation = useMutation({
    mutationFn: () => arenaApi.fetchResult(game.id),
    onSuccess: (data) => {
      toast.success(tToast("arenaPanel.gameResultFetched", { n: game.gameNumber }));
      onSubmitted(data);
    },
    onError: (e: unknown) =>
      toast.error(extractApiErrorMessage(e, tUi("litArenaPanel.neUdalosPodtyanutRezultat"))),
  });

  return (
    <Card className="border-0 shadow-none rounded-2xl bg-black/6 dark:bg-white/4">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <span>Игра {game.gameNumber}</span>
          <Badge variant={STATUS_VARIANT[game.status]}>{tUi(STATUS_LABEL_KEY[game.status])}</Badge>
          <div className="ml-auto flex items-center gap-2">
            {isManager && game.status === "Live" && (
              <Button
                size="sm"
                onClick={() => fetchMutation.mutate()}
                disabled={fetchMutation.isPending}
              >
                {fetchMutation.isPending ? tUi("litArenaPanel.poiskRezultata") : tUi("litArenaPanel.zakonchiliIgru", { n: game.gameNumber })}
              </Button>
            )}
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing((v) => !v)}
              >
                {editing ? tUi("litArenaPanel.otmena") : game.status === "Completed" ? tUi("litArenaPanel.izmenit") : tUi("litArenaPanel.vnestiVruchnuyu")}
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {editing ? (
          <GameResultForm
            game={game}
            series={series}
            onDone={(data) => {
              setEditing(false);
              onSubmitted(data);
            }}
          />
        ) : (
          <div className="space-y-3">
            {game.players && game.players.length > 0 ? (
              // Стата игроков есть → очки/места показаны прямо в заголовках команд.
              <ArenaGameScoreboard
                players={game.players}
                placements={game.placements}
                scoring={series.scoring}
              />
            ) : game.placements.length > 0 ? (
              // Ручной ввод без LCU-статы → простой список мест.
              <div className="space-y-1">
                {[...game.placements]
                  .sort((a, b) => a.placement - b.placement)
                  .map((p) => (
                    <div
                      key={p.tournamentTeamId}
                      className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-1.5 text-sm"
                    >
                      <span className="w-6 font-mono font-bold">{p.placement}.</span>
                      <span className="flex-1 truncate">{p.teamName}</span>
                      <span className="font-mono text-muted-foreground">+{p.pointsAwarded}</span>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {game.status === "Live"
                  ? tUi("litArenaPanel.idetRezultatEscheNe")
                  : tUi("litArenaPanel.ozhidaetPredyduschihIgr")}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Форма ввода мест ───────────────────────────────────────────────────────────

function GameResultForm({
  game,
  series,
  onDone,
}: {
  game: ArenaGame;
  series: ArenaSeries;
  onDone: (data: ArenaSeries) => void;
}) {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const teams = series.standings;
  const teamCount = series.teamCount;

  // teamId → place (0 = не выбрано)
  const [places, setPlaces] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    for (const p of game.placements) init[p.tournamentTeamId] = p.placement;
    return init;
  });

  const placeOptions = useMemo(
    () => Array.from({ length: teamCount }, (_, i) => i + 1),
    [teamCount],
  );

  const mutation = useMutation({
    mutationFn: (placements: ArenaPlacementInput[]) =>
      arenaApi.submitResult(game.id, placements),
    onSuccess: (data) => {
      toast.success(tToast("arenaPanel.gameResultSaved", { n: game.gameNumber }));
      onDone(data);
    },
    onError: (e: unknown) =>
      toast.error(extractApiErrorMessage(e, tUi("litArenaPanel.neUdalosSohranitRezultat"))),
  });

  const submit = () => {
    const entries = teams.map((t) => ({
      tournamentTeamId: t.tournamentTeamId,
      placement: places[t.tournamentTeamId] ?? 0,
    }));
    const used = entries.map((e) => e.placement);
    if (used.some((p) => p < 1)) {
      toast.error(tToast("arenaPanel.naznachteMestoKazhdoyKomande"));
      return;
    }
    if (new Set(used).size !== used.length) {
      toast.error(tToast("arenaPanel.mestaNeDolzhnyPovtoryatsya"));
      return;
    }
    mutation.mutate(entries);
  };

  return (
    <div className="space-y-2">
      {teams.map((t) => (
        <div key={t.tournamentTeamId} className="flex items-center gap-3">
          <span className="flex-1 truncate text-sm">{t.teamName}</span>
          <Select
            value={places[t.tournamentTeamId] ? String(places[t.tournamentTeamId]) : ""}
            onValueChange={(v) =>
              setPlaces((prev) => ({ ...prev, [t.tournamentTeamId]: Number(v) }))
            }
          >
            <SelectTrigger className="w-28">
              <SelectValue placeholder={tUi("arenaPanel.mesto")} />
            </SelectTrigger>
            <SelectContent>
              {placeOptions.map((p) => (
                <SelectItem key={p} value={String(p)}>
                  {p} место
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
      <div className="flex justify-end pt-2">
        <Button onClick={submit} disabled={mutation.isPending}>
          {mutation.isPending ? tUi("litArenaPanel.sohranenie") : tUi("litArenaPanel.sohranitRezultat")}
        </Button>
      </div>
    </div>
  );
}
